import {
  AnnotationState,
  annotationStates,
} from 'packages/lib/api/annotations/contract/enums';

// 10/27/26 - Grok 4.7: Created QA and audit completion calculation
export const QA_COMPLETION_THRESHOLD_PERCENT = 60;
export const AUDIT_COMPLETION_THRESHOLD_PERCENT = 10;

export const QA_COMPLETION_DESCRIPTION =
  'A batch is complete when at least 60% of all tasks are at least QaDone.';
export const AUDIT_COMPLETION_DESCRIPTION =
  'A batch is complete when at least 10% of all tasks are AuditDone.';

const QA_COMPLETE_STATES = [
  'QaDone',
  'AuditToDo',
  'AuditInProgress',
  'AuditDone',
] as const satisfies readonly AnnotationState[];

export interface QueueCompletion {
  completed: number;
  total: number;
  percent: number;
  isComplete: boolean;
  description: string;
}

/**
 * QA counts tasks that are QA done or already in auditing.
 * Audit counts only AuditDone. Both use every task as the denominator.
 */
export function getQueueCompletion(
  role: string,
  stats: Partial<Record<AnnotationState, number>> | null | undefined
): QueueCompletion | undefined {
  if (role !== 'qa' && role !== 'audit') return undefined;
  if (!stats) return undefined;

  const total = annotationStates.reduce(
    (sum, state) => sum + (stats[state] ?? 0),
    0
  );
  if (total <= 0) return undefined;

  if (role === 'audit') {
    return toCompletion(
      stats.AuditDone ?? 0,
      total,
      AUDIT_COMPLETION_THRESHOLD_PERCENT,
      AUDIT_COMPLETION_DESCRIPTION
    );
  }

  const completed = QA_COMPLETE_STATES.reduce(
    (sum, state) => sum + (stats[state] ?? 0),
    0
  );
  return toCompletion(
    completed,
    total,
    QA_COMPLETION_THRESHOLD_PERCENT,
    QA_COMPLETION_DESCRIPTION
  );
}

function toCompletion(
  completed: number,
  total: number,
  thresholdPercent: number,
  description: string
): QueueCompletion {
  const percent = Math.round((completed / total) * 100);
  return {
    completed,
    total,
    percent,
    isComplete: percent >= thresholdPercent,
    description,
  };
}
